import _ from 'lodash';
import {
  SLTXFormatRootFile,
  SLTXFormatRootFileGroup,
} from '../../fileFormats/sltx/sltx';

import { ModelTreeNode, ParsedTreeNodeType } from '../data/modelOptionsTree';
import { TreeOptionSelections } from '../data/optionsSelection';

import { RulesEvaluationFilter } from '../rules/rulesEvaluationFilter';
import { RulesEvaluationDomains } from '../rules/rulesEvaluationDomains';

import { ModelRuleType } from '../rules/modelRuleType';
import { ModelRuleData } from '../rules/modelRuleData';

import { ModelParser } from './modelParser';
import { LayoutUtil } from './layoutUtil';
import { RulesEvaluationDependencies } from '../rules/rulesEvaluationDependencies';
import { SLTXUtil } from '../../fileFormats/sltx/sltxUtil';

export class TreeBuilding {

  public static buildTree(
    rules : ModelRuleData[],
    sltxFile : SLTXFormatRootFile,
    optionsSelectionState : TreeOptionSelections,
  ) : ModelTreeNode | undefined {

    // Grab the groups from the file

    const fileGroups = sltxFile.groups;
    if (fileGroups === undefined) {
      return undefined;
    }

    const fileGroupsAfterFiltering = this.applyFilteringRules(
      this.getActiveFilteringRules(rules),
      fileGroups,
      optionsSelectionState,
      sltxFile,
    );

    // Build the tree
    const tree = ModelParser.buildModelTreeFromGroups(fileGroupsAfterFiltering);
    this.orderOptions(tree);

    // Apply the domain rules to the tree
    const treeAfterDomainRules = this.applyDomainRules(
      this.getActiveDomainRules(rules),
      tree,
      sltxFile,
    );

    // Build the paths to all the nodes
    this.buildPathsForTree(treeAfterDomainRules);
    this.buildSltxgPaths(
      treeAfterDomainRules,
      sltxFile,
      undefined,
    );

    // Apply the filtering rules to the SLTX file
    const treeAfterDependenciesRules = this.applyDependenciesRules(
      this.getActiveDependenciesRules(rules),
      optionsSelectionState,
      treeAfterDomainRules,
      sltxFile,
    );

    this.buildPathsForTree(treeAfterDependenciesRules);
    this.buildSltxgPaths(
      treeAfterDependenciesRules,
      sltxFile,
      undefined,
    );
    this.checkSingleChildBranches(treeAfterDependenciesRules);
    this.orderOptions(treeAfterDependenciesRules);
    return treeAfterDependenciesRules;
  }

  private static getActiveFilteringRules(rules : ModelRuleData[]) : ModelRuleData[] {
    return this.getActiveRules(ModelRuleType.Filtering, rules);
  }

  private static getActiveDomainRules(rules : ModelRuleData[]) : ModelRuleData[] {
    return this.getActiveRules(ModelRuleType.Domain, rules);
  }

  private static getActiveDependenciesRules(rules : ModelRuleData[]) : ModelRuleData[] {
    return this.getActiveRules(ModelRuleType.Dependencies, rules);
  }

  private static getActiveRules(
    ruleType : ModelRuleType,
    rules : ModelRuleData[],
  ) : ModelRuleData[] {

    // Grab all the rules of the specified type that are also active
    const activeRules = rules.filter((rule) => rule.enabled && rule.type === ruleType);
    return activeRules;
  }

  private static applyFilteringRules(
    filteringRules : ModelRuleData[],
    originalGroups: SLTXFormatRootFileGroup[],
    optionsSelectionState : TreeOptionSelections,
    sltxFile : SLTXFormatRootFile,
  ) : SLTXFormatRootFileGroup[] {

    // Clone the original groups
    let modelTreeAfterFilters = _.cloneDeep(originalGroups);

    // Go through all the rules and evaluate them
    filteringRules.forEach((rule) => {

      modelTreeAfterFilters = RulesEvaluationFilter.evaluateRule(
        rule,
        modelTreeAfterFilters,
        optionsSelectionState,
        sltxFile,
      );
    });

    return modelTreeAfterFilters;
  }

  private static applyDomainRules(
    domainRules : ModelRuleData[],
    tree : ModelTreeNode,
    sltxFile : SLTXFormatRootFile,
  ) {

    let adjustedTree = _.cloneDeep(tree);

    // --------------------------------------------------------
    // Let's order the rules based on their priority,
    // so the ones with higher priority are applied later
    // and can count the previous ones being already applied
    // --------------------------------------------------------
    domainRules.sort((a, b) => {
      if (a.priority === undefined && b.priority === undefined) {
        return (a.ruleID || 0) - (b.ruleID || 0);
      }
      if (a.priority === undefined) {
        return 1;
      }
      if (b.priority === undefined) {
        return -1;
      }
      if (a.priority === b.priority) {
        return (a.ruleID || 0) - (b.ruleID || 0);
      }
      return a.priority - b.priority;
    });

    // Go through all the rules and evaluate them
    domainRules.forEach((rule) => {

      adjustedTree = RulesEvaluationDomains.evaluateRule(
        rule,
        adjustedTree,
        sltxFile,
      );
    });

    return adjustedTree;
  }

  private static applyDependenciesRules(
    dependenciesRules : ModelRuleData[],
    optionsSelectionState : TreeOptionSelections,
    modelTree: ModelTreeNode,
    sltxFile : SLTXFormatRootFile,
  ) {

    let adjustedTree = _.cloneDeep(modelTree);

    // Save the current state of selection as paths
    adjustedTree.selected = RulesEvaluationDependencies.getPathsFromOptionSelection(
      sltxFile,
      optionsSelectionState,
    );

    // --------------------------------------------------------
    // Let's order the rules based on their priority,
    // so the ones with higher priority are applied later
    // and can count the previous ones being already applied
    // --------------------------------------------------------
    dependenciesRules.sort((a, b) => {
      if (a.priority === undefined && b.priority === undefined) {
        return (a.ruleID || 0) - (b.ruleID || 0);
      }
      if (a.priority === undefined) {
        return 1;
      }
      if (b.priority === undefined) {
        return -1;
      }
      if (a.priority === b.priority) {
        return (a.ruleID || 0) - (b.ruleID || 0);
      }
      return a.priority - b.priority;
    });

    // Go through all the rules and evaluate them
    dependenciesRules.forEach((rule) => {
      adjustedTree = RulesEvaluationDependencies.evaluateRule(
        rule,
        optionsSelectionState,
        adjustedTree,
        sltxFile,
      );
    });

    return adjustedTree;
  }

  private static orderOptions(node : ModelTreeNode) {

    if (node.children.length && node.children[0].type === ParsedTreeNodeType.Option) {
      node.children.sort((a, b) => {
        const priorityOrder = ['No', 'None'];
        const alwaysLast = SLTXUtil.DummyOption;

        const aPriority = priorityOrder.indexOf(a.name);
        const bPriority = priorityOrder.indexOf(b.name);

        if (aPriority >= 0 && bPriority >= 0) {
          return aPriority - bPriority;
        }
        if (aPriority >= 0) {
          return -1;
        }
        if (bPriority >= 0) {
          return 1;
        }
        // Handle "Reiniciar Reset"
        if (a.name === alwaysLast && b.name === alwaysLast) {
          return 0;
        }
        if (a.name === alwaysLast) {
          return 1;
        }
        if (b.name === alwaysLast) {
          return -1;
        }

        return a.name.localeCompare(b.name);
      });
    }
    node.children.forEach((child) => {
      this.orderOptions(child);
    });
  }

  private static buildPathsForTree(treeRoot : ModelTreeNode) {

    // eslint-disable-next-line no-param-reassign
    treeRoot.path = '';

    treeRoot.children.forEach((child) => {
      this.buildPaths(child, '');
    });
  }

  private static buildPaths(node : ModelTreeNode, pathParent = '') {
    // eslint-disable-next-line no-param-reassign
    node.path = LayoutUtil.buildPathForChild(pathParent, node);

    node.children.forEach((child) => {
      this.buildPaths(child, node.path);
    });
  }

  private static buildSltxgPaths(
    treeRoot : ModelTreeNode,
    sltxFile: SLTXFormatRootFile,
    groupId: number | undefined,
  ) {
    let actualGroupId : number | undefined = groupId;

    // eslint-disable-next-line no-param-reassign
    treeRoot.model = sltxFile.name;
    if (treeRoot.type === ParsedTreeNodeType.Group) {
      actualGroupId = treeRoot.id;
      // eslint-disable-next-line no-param-reassign
      treeRoot.sltxPath = `${treeRoot.name}`;
    } else if (actualGroupId !== undefined && treeRoot.type === ParsedTreeNodeType.Folder) {
      // eslint-disable-next-line no-param-reassign
      treeRoot.sltxPath = this.buildConfigPath(
        sltxFile,
        actualGroupId,
        undefined,
        treeRoot.name,
      );
    } else if (actualGroupId !== undefined && treeRoot.type === ParsedTreeNodeType.Option) {
      // eslint-disable-next-line no-param-reassign
      treeRoot.sltxPath = this.buildConfigPath(
        sltxFile,
        actualGroupId,
        treeRoot.id,
        undefined,
      );
    }
    treeRoot.children.forEach((child) => this.buildSltxgPaths(
      child,
      sltxFile,
      actualGroupId,
    ));
  }

  // Get the sltx configuration path
  private static buildConfigPath(
    sltxFile: SLTXFormatRootFile,
    groupId: number,
    optionId: number | undefined,
    folderName: string | undefined,
  ) : string | undefined {

    const group = sltxFile.groups?.find((g) => g.id === groupId);
    if (!group) return undefined;

    for (let i = 0; i < group.optionsFolders.length; i++) {
      const folder = group.optionsFolders[i];
      if (optionId !== undefined) { // If optionId is defined we're looking base on it
        const option = folder.options.find((opt) => opt.id === optionId);
        if (option !== undefined) return folder.name !== 'Root' ? `${group.name}/${folder.name}/${option.name}` : `${group.name}/${option.name}`;
      } else if (folderName && folderName === folder.name) {
        return `${group.name}/${folder.name}`;
      }
    }
    return undefined;
  }

  public static checkSingleChildBranches(
    node : ModelTreeNode,
  ) {
    if (node.children.length === 0) {
      return true;
    }
    if (node.children.length === 1) {
      // If the node has only one child, check the single child recursively
      const isSingleOption = TreeBuilding.checkSingleChildBranches(node.children[0]);
      // Mark the current node as singleOption if the child is also singleOption
      if (isSingleOption) {
        // eslint-disable-next-line no-param-reassign
        node.singleOptionParent = true;
        return true;
      }
      // eslint-disable-next-line no-param-reassign
      node.singleOptionParent = false;
    }
    if (node.children.length === 2) {
      // If the node has only two child, check the single child recursively
      if (node.children[0].name === SLTXUtil.DummyOption
        || node.children[1].name === SLTXUtil.DummyOption) {
        // eslint-disable-next-line no-param-reassign
        node.singleOptionParent = true;
        return true;
      }
    }
    node.children.forEach((n) => TreeBuilding.checkSingleChildBranches(n));
    return false;
  }

  public static filterTopSingleChildBranches(
    root : ModelTreeNode,
  ) : ModelTreeNode {
    const newRoot = _.cloneDeep(root);
    const newChildrenWithMultipleBranches = root.children.filter(
      (node) => node.singleOptionParent !== true,
    );
    const newChildrenWithSingleBranches = root.children.filter(
      (node) => node.singleOptionParent === true,
    );
    // eslint-disable-next-line no-param-reassign
    newRoot.children = _.cloneDeep(newChildrenWithMultipleBranches);
    newRoot.children.concat(_.cloneDeep(newChildrenWithSingleBranches));
    return newRoot;
  }

  public static getNumberOfMultipleBranchChidlren(
    root : ModelTreeNode,
  ) : number {
    return root.children.filter(
      (node) => node.singleOptionParent !== true,
    ).length;
  }
  public static getNumberOfMultipleBranchChidlrenTillPage(
    root : ModelTreeNode,
    realPage: number,
    hasCustomAttrs: boolean,
  ) : number {
    let count = 0;
    let curPage = 1;
    root.children.forEach((n) => {
      if (curPage <= realPage && !n.singleOptionParent) {
        count++;
      }
      curPage++;
    });
    if (hasCustomAttrs && realPage >= (curPage)) return count + 1;
    return count;
  }

  // Debug functions
  public static dumpTreeNode(
    tree: ModelTreeNode,
    tabs: number,
  ) {
    let tabspaces = '  ';
    for (let i = 0; i < tabs; i++) tabspaces += '  ';
    console.log(`t=${tabspaces}${tree.path}`);
    console.log(`c=${tabspaces}${tree.sltxPath}`);
    tree.children.forEach((child : ModelTreeNode) => this.dumpTreeNode(child, tabs + 1));
  }
}
